import {
  useContext,
  forwardRef,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

import TicketsListSkeleton from "../TicketsListSkeleton";
import TicketListItem from "../TicketListItemCustom";

import { i18n } from "../../translate/i18n";

import { AuthContext } from "../../context/Auth/AuthContext";

const ITEM_HEIGHT = 68;
const OVERSCAN = 8;

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flex: 1,
    flexDirection: "column",
    borderTopRightRadius: 0,
    background: theme.palette.background.default,
    boxShadow: "none",
  },

  ticketsList: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    background: theme.palette.background.default,
  },

  ticketsViewport: {
    position: "relative",
    width: "100%",
  },

  ticketRow: {
    position: "absolute",
    left: 0,
    right: 0,
    padding: "3px 8px",
    boxSizing: "border-box",
    display: "flex",
  },

  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  loadingMore: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    padding: 16,
    color: "#666",
    fontSize: 13,
  },

  spinner: {
    width: 14,
    height: 14,
    border: "2px solid #eee",
    borderTop: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "50%",
    animation: "$spin 0.8s linear infinite",
  },

  "@global": {
    "@keyframes spin": {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" },
    },
  },
}));

const TicketsListCustom = forwardRef((props, ref) => {
  const {
    tickets,
    hasMore,
    loading,
    pageNumber,
    dispatch,
    setPageNumber,
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { ticketId } = useParams();
  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(480);

  const handleSelectTicket = useCallback((selectedTicketId) => {
    history.push(`/tickets/${selectedTicketId}`);
  }, [history]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      setViewportHeight(el.clientHeight || 480);
    });
    observer.observe(el);
    setViewportHeight(el.clientHeight || 480);

    return () => observer.disconnect();
  }, []);

  const handleScroll = (e) => {
    const { scrollTop: nextScrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      setScrollTop(nextScrollTop);
    });

    if (scrollHeight - nextScrollTop <= clientHeight + 80 && hasMore && !loading) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const isInitialLoading = loading && (!tickets || tickets.length === 0);
  const showLoadMore = loading && tickets?.length > 0 && hasMore && pageNumber > 1;
  const ticketCount = tickets?.length || 0;
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
  const visibleCount = Math.ceil(viewportHeight / ITEM_HEIGHT) + OVERSCAN * 2;
  const endIndex = Math.min(ticketCount, startIndex + visibleCount);
  const visibleTickets = ticketCount > 0 ? tickets.slice(startIndex, endIndex) : [];

  return (
    <Paper elevation={0} className={classes.ticketsListWrapper} ref={ref}>
      <Paper
        square
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
        ref={scrollRef}
      >
        {isInitialLoading ? (
          <TicketsListSkeleton />
        ) : ticketCount === 0 ? (
          <div className={classes.noTicketsDiv}>
            <span className={classes.noTicketsTitle}>
              {i18n.t("ticketsList.noTicketsTitle")}
            </span>
            <p className={classes.noTicketsText}>
              {i18n.t("ticketsList.noTicketsMessage")}
            </p>
          </div>
        ) : (
          <>
            <div
              className={classes.ticketsViewport}
              style={{ height: ticketCount * ITEM_HEIGHT }}
            >
              {visibleTickets.map((ticket, index) => (
                <div
                  key={ticket.id}
                  className={classes.ticketRow}
                  style={{
                    top: (startIndex + index) * ITEM_HEIGHT,
                    height: ITEM_HEIGHT,
                  }}
                >
                  <TicketListItem
                    ticket={ticket}
                    isSelected={ticket.uuid === ticketId}
                    onSelectTicket={handleSelectTicket}
                    dispatch={dispatch}
                    currentUserId={user?.id}
                  />
                </div>
              ))}
            </div>
            {showLoadMore && (
              <div className={classes.loadingMore}>
                <div className={classes.spinner} />
                Cargando más tickets...
              </div>
            )}
          </>
        )}
      </Paper>
    </Paper>
  );
});

export default TicketsListCustom;
